import { useCallback, useContext, useRef } from "react";

import { QuestionBan } from "types/socket-messages";
import { IncomingSocketQuestion, PaginatedQuestion, SqsSocketTypes, Status } from "@/types/working-model";
import {
	appendOneQuestionsComment,
	handleBan as handleBanAction,
	prependQuestions,
	questionCommentApprove,
	questionCommentDelete,
	questionCommentUnapprove,
	questionDelete,
	questionLike,
	questionUnapprove,
	questionUnlike,
	questionUpsert,
	questionsAnsweredBulk,
	QuestionsContext,
} from "../../../panel/contexts/engage/questions-state";
import { assertIsAnsweredQuestion, assertIsDeleteQuestionComment, assertIsIncomingSocketQuestion, assertIsMessage, assertIsQuestionBan, assertIsQuestionLike, assertIsQuestionSocketDeleteType, assertIsQuestionStatusData, assertIsSocketQuestionComment } from "./question-socket.assertions";

// Socket payloads are untyped at the network boundary — cast fields individually.

type SocketQuestionInput = Partial<IncomingSocketQuestion> & {
	id: number;
	avatar?: string | null;
	pending_moderator?: boolean;
};

const toPaginatedQuestion = (data: SocketQuestionInput, channel: number | undefined): PaginatedQuestion => ({
	answered: !!data?.answered,
	avatar: (data?.avatar ?? (channel != null ? (data as Record<number, { avatar?: string } | undefined>)?.[channel]?.avatar : undefined) ?? null) as string | null,
	bl_profile: (data?.bl_profile ?? null) as number | null,
	bl_profile_non_registered: (data?.bl_profile_non_registered ?? null) as number | null,
	blocked: (data?.blocked ?? null) as boolean | null,
	blocked_by: (data?.blocked_by ?? null) as number | null,
	blocked_at: (data?.blocked_at ?? null) as string | null,
	blocked_reason: data?.blocked_reason ?? null,
	comments: 0,
	created_at: (data?.created_at ?? new Date().toISOString()) as string,
	first_name: data?.first_name as string,
	id: data?.id as number,
	language: (data?.language ?? 'en') as string,
	last_name: data?.last_name as string,
	likes: 0,
	question: (data?.question ?? '') as string,
	spam: !!data?.spam,
	module_id: (data?.module_id ?? 0) as number,
	status: (data?.status ?? Status.Approved) as Status,
	pending_moderator: !!data?.pending_moderator,
});

/**
 * Returns a stable `onMessage` callback suitable for passing to `useQuestionsSocketsAdmin`.
 * Because the callback never changes reference, the socket hook's effect won't re-run on
 * every parent render, preventing repeated listener teardown/re-registration.
 */
const useOnIncomingSocketMessages = (channel: number | undefined) => {
	const [, dispatch] = useContext(QuestionsContext);

	// Keep dispatch and channel in a ref so the stable callback always reads the latest
	// values without needing to be recreated.
	const stateRef = useRef({ dispatch, channel });
	stateRef.current = { dispatch, channel };

	return useCallback((type: string, data: unknown) => {
		const { dispatch: d, channel: ch } = stateRef.current;

		switch (type) {
			case SqsSocketTypes.QUESTION: {
				try {
					assertIsIncomingSocketQuestion(data);
				} catch (error) {
					console.error(error);
					return;
				}
				d(prependQuestions([toPaginatedQuestion(data, ch)]));
				break;
			}

			case SqsSocketTypes.QUESTION_COMMENT: {
				try {
					assertIsSocketQuestionComment(data);
				} catch (error) {
					console.error(error);
					return;
				}
				d(appendOneQuestionsComment(data.question_id, [data]));
				break;
			}

			case SqsSocketTypes.QUESTION_LIKE: {
				try {
					assertIsQuestionLike(data);
				} catch (error) {
					console.error(error);
					return;
				}
				const likeId = data.question_id;
				d(questionLike(likeId));
				break;
			}

			case SqsSocketTypes.QUESTION_UNLIKE: {
				try {
					assertIsQuestionLike(data);
				} catch (error) {
					console.error(error);
					return;
				}
				const unlikeId = data.question_id;
				d(questionUnlike(unlikeId));
				break;
			}

			case 'delete_question': {
				try {
					assertIsMessage(data);
					assertIsQuestionSocketDeleteType(data.data);
				} catch (error) {
					console.error(error);
					return;
				}
				d(questionDelete(data.data.id));
				break;
			}

			case 'undelete_question': {
				try {
					assertIsMessage(data);
					assertIsIncomingSocketQuestion(data.data);
				} catch (error) {
					console.error(error);
					return;
				}
				d(questionUpsert(toPaginatedQuestion(data.data, ch)));
				break;
			}

			case 'delete_question_comment': {
				try {
					assertIsMessage(data);
					assertIsDeleteQuestionComment(data.data);
				} catch (error) {
					console.error(error);
					return;
				}
				d(questionCommentDelete(data.data.comment_id, data.data.question_id));
				break;
			}

			case 'undelete_question_comment': {
				try {
					assertIsMessage(data);
					assertIsSocketQuestionComment(data.data);
				} catch (error) {
					console.error(error);
					return;
				}
				d(appendOneQuestionsComment(data.data.question_id, [data.data as unknown as IncomingSocketQuestion]));
				break;
			}

			case 'bulk_answer': {
				try {
					assertIsMessage(data);
					assertIsAnsweredQuestion(data.data);
				} catch (error) {
					console.error(error);
					return;
				}
				d(questionsAnsweredBulk(data.data.question_ids, data.data.answered));
				break;
			}

			case 'ban': {
				try {
					assertIsMessage(data);
				} catch (error) {
					console.error(error);
					return;
				}
				if (!Array.isArray(data.data)) return;
				const validBans: QuestionBan[] = [];
				for (const item of data.data) {
					try {
						assertIsQuestionBan(item);
						validBans.push(item);
					} catch (error) {
						console.error(error);
					}
				}
				if (validBans.length) d(handleBanAction(validBans));
				break;
			}

			case 'question_status': {
				try {
					assertIsMessage(data);
					assertIsQuestionStatusData(data.data);
				} catch (error) {
					console.error(error);
					return;
				}
				if (data.data.status === Status.Approved) {
					d(questionUpsert(toPaginatedQuestion(data.data, ch)));
				} else {
					d(questionUnapprove(data.data.id));
				}
				break;
			}

			case 'question_comment_status': {
				try {
					assertIsMessage(data);
					assertIsQuestionStatusData(data.data);
				} catch (error) {
					console.error(error);
					return;
				}
				if (data.data.status === Status.Approved) {
					d(questionCommentApprove(data.data.id));
				} else {
					d(questionCommentUnapprove(data.data.id));
				}
				break;
			}

			default:
				break;
		}
		// Do not pass any deps arrays in order to keep this stable — all values read through stateRef
	}, []);
};

export default useOnIncomingSocketMessages;
